import axios from 'axios';
import {
  POST_ONLY_ROUTES,
  GET_SAFE_ROUTES,
  detectInvalidMethodOnRoute,
  createMethodViolationError,
  isPostOnlyRoute,
} from './postOnlyRoutes';

// =============================================================================
// P0 PROD FIX: BUILD MARKER FOR CACHE BUSTING
// =============================================================================
const BUILD_MARKER = '20260216_p0_x_custom_app_credentials_restored';
if (typeof window !== 'undefined') {
  window.__BUILD_MARKER__ = BUILD_MARKER;
  console.info(`[BUILD] __BUILD_MARKER__=${BUILD_MARKER}`);
  
  // P0 iOS Safari DEFINITIVE: Expose testAuthEcho function for debugging
  // Usage from Safari console: window.testAuthEcho()
  // Uses apiClient to ensure same behavior as real API calls
  window.testAuthEcho = async function() {
    const token = localStorage.getItem('token');
    console.log('[testAuthEcho] Token in localStorage:', token ? 'present' : 'NONE');
    
    try {
      // Use apiClient (same as all other calls) - includes withCredentials and dual-headers
      const response = await window.__apiClient?.get('/auth/echo') || 
        await fetch('/api/auth/echo', { credentials: 'include' }).then(r => r.json()).then(data => ({ data }));
      
      const data = response.data || response;
      
      console.log('[testAuthEcho] === AUTH ECHO RESULTS ===');
      console.log('Authorization header:', data.has_authorization_header);
      console.log('X-AME-Authorization header:', data.has_x_ame_authorization_header);
      console.log('Cookie (ame_session):', data.has_cookie_session);
      console.log('Auth source (would be used):', data.auth_source);
      console.log('Request ID:', data.request_id);
      
      if (data.auth_source !== 'none') {
        console.log(`[testAuthEcho] ✅ SUCCESS: Auth available via ${data.auth_source}`);
        if (data.auth_source === 'cookie' && !data.has_authorization_header) {
          console.warn('[testAuthEcho] ⚠️ Headers stripped by Safari, but cookie fallback working!');
        }
      } else if (token) {
        console.error('[testAuthEcho] ❌ BUG: Token in localStorage but NO auth reached backend!');
        console.error('[testAuthEcho] Try logging out and back in to set the cookie.');
      } else {
        console.info('[testAuthEcho] ℹ️ No token in localStorage - not logged in');
      }
      
      return data;
    } catch (e) {
      console.error('[testAuthEcho] Error:', e);
      return { error: e.message };
    }
  };
  
  // P0 FIX: Migrate selected_project_ic -> selected_project_id (typo fix)
  try {
    const legacyProjectId = localStorage.getItem('selected_project_ic');
    const currentProjectId = localStorage.getItem('selected_project_id');
    if (legacyProjectId && !currentProjectId) {
      localStorage.setItem('selected_project_id', legacyProjectId);
      console.info('[BUILD] Migrated selected_project_ic -> selected_project_id');
    }
  } catch (e) {
    // Ignore localStorage errors
  }
}

// =============================================================================
// P0 FIX: REAUTH EVENT FOR AUTH CONTEXT
// =============================================================================
export const REAUTH_EVENT = 'ame:reauth-required';

/**
 * P0 FIX: Dispatch reauth event for AuthContext to listen
 */
export function dispatchReauthRequired(reason = 'token_expired') {
  if (typeof window !== 'undefined') {
    console.warn(`[AUTH] Dispatching ${REAUTH_EVENT} reason=${reason}`);
    window.dispatchEvent(new CustomEvent(REAUTH_EVENT, { detail: { reason } }));
  }
}

// =============================================================================
// P0 FIX: PROJECT ID RESOLUTION - STOP PLACEHOLDER/INVALID IDS
// =============================================================================

/**
 * P0 FIX: Check if a projectId is a placeholder or invalid
 * Placeholders like "<TON_PROJECT_ID>" or "%3CTON_PROJECT_ID%3E" should be rejected
 * Must match UUID v4 format for valid IDs
 */
export function isInvalidProjectId(projectId) {
  if (!projectId || typeof projectId !== 'string') return true;
  const trimmed = projectId.trim();
  if (!trimmed) return true;
  
  // Check for URL-encoded or raw placeholders
  if (trimmed.includes('<') || trimmed.includes('>')) return true;
  if (trimmed.includes('%3C') || trimmed.includes('%3E')) return true;
  if (trimmed.toLowerCase().includes('project_id')) return true;
  if (trimmed.toLowerCase().includes('ton_project')) return true;
  if (trimmed === 'undefined' || trimmed === 'null') return true;
  
  // P0 FIX: Require UUID v4 format
  const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (!uuidV4Regex.test(trimmed)) {
    console.warn(`[PROJECT_ID] Invalid format (not UUID v4): ${trimmed.substring(0, 20)}`);
    return true;
  }
  
  return false;
}

/**
 * P0 FIX: Get the best available projectId with fallback chain
 * Priority: routeParam > localStorage > sessionStorage
 * Returns null if no valid projectId found
 * 
 * Note: For async fallback to /api/auth/me, use resolveProjectIdAsync()
 */
export function resolveProjectId(routeParam) {
  // Priority 1: Route parameter (if valid)
  if (routeParam && !isInvalidProjectId(routeParam)) {
    console.info(`[PROJECT_ID] resolved from route: ${routeParam.substring(0, 8)}...`);
    // Also save to localStorage for persistence
    try {
      localStorage.setItem('selected_project_id', routeParam);
      sessionStorage.setItem('selected_project_id', routeParam);
    } catch (e) { /* ignore */ }
    return routeParam;
  }
  
  // Priority 2: localStorage
  try {
    const stored = localStorage.getItem('selected_project_id');
    if (stored && !isInvalidProjectId(stored)) {
      console.info(`[PROJECT_ID] resolved from localStorage: ${stored.substring(0, 8)}...`);
      return stored;
    }
  } catch (e) { /* ignore */ }
  
  // Priority 3: sessionStorage (fallback for some iOS browsers)
  try {
    const session = sessionStorage.getItem('selected_project_id');
    if (session && !isInvalidProjectId(session)) {
      console.info(`[PROJECT_ID] resolved from sessionStorage: ${session.substring(0, 8)}...`);
      return session;
    }
  } catch (e) { /* ignore */ }
  
  console.warn('[PROJECT_ID] ⚠️ No valid projectId found in sync sources');
  return null;
}

/**
 * P0 FIX: Async version that includes /api/auth/me fallback
 * Only call this when sync resolution fails
 */
export async function resolveProjectIdAsync(routeParam, apiClient) {
  // First try sync resolution
  const syncResult = resolveProjectId(routeParam);
  if (syncResult) return syncResult;
  
  // Priority 4: /api/auth/me last_project_id
  try {
    console.info('[PROJECT_ID] Trying /api/auth/me fallback...');
    const response = await apiClient.get('/auth/me');
    const lastProjectId = response.data?.last_project_id;
    if (lastProjectId && !isInvalidProjectId(lastProjectId)) {
      console.info(`[PROJECT_ID] resolved from /api/auth/me: ${lastProjectId.substring(0, 8)}...`);
      persistProjectId(lastProjectId);
      return lastProjectId;
    }
  } catch (e) {
    console.warn('[PROJECT_ID] /api/auth/me fallback failed:', e.message);
  }
  
  console.warn('[PROJECT_ID] ⚠️ No valid projectId found (all sources exhausted)');
  return null;
}

/**
 * P0 FIX: Save projectId to persistent storage after successful load
 */
export function persistProjectId(projectId) {
  if (!projectId || isInvalidProjectId(projectId)) return;
  
  try {
    localStorage.setItem('selected_project_id', projectId);
    sessionStorage.setItem('selected_project_id', projectId);
    console.info(`[PROJECT_ID] persisted: ${projectId.substring(0, 8)}...`);
  } catch (e) {
    console.warn('[PROJECT_ID] Failed to persist:', e.message);
  }
}

/**
 * P0 FIX: Clear projectId from storage (on logout or project delete)
 */
export function clearPersistedProjectId() {
  try {
    localStorage.removeItem('selected_project_id');
    sessionStorage.removeItem('selected_project_id');
    console.info('[PROJECT_ID] cleared from storage');
  } catch (e) { /* ignore */ }
}

// =============================================================================
// PRODUCTION CONFIGURATION - HARDCODED, NON-NEGOTIABLE
// =============================================================================
const PRODUCTION_DOMAIN = 'ai-marketing-engine.com';
const PRODUCTION_API_URL = 'https://ai-marketing-engine.com';

// Forbidden patterns - ONLY in production mode
// Preview URLs are allowed in development
const FORBIDDEN_IN_PRODUCTION = [
  /localhost/i,
  /127\.0\.0\.1/i,
];

// =============================================================================
// ENVIRONMENT DETECTION - USE NODE_ENV AS PRIMARY SIGNAL
// =============================================================================
const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// FAIL-FAST PROTECTION - ONLY FOR PRODUCTION
// =============================================================================
function validateProductionConfig() {
  if (!IS_PRODUCTION) return true;
  
  // In production, REACT_APP_BACKEND_URL should either be empty or point to production
  if (ENV_BACKEND_URL) {
    for (const pattern of FORBIDDEN_IN_PRODUCTION) {
      if (pattern.test(ENV_BACKEND_URL)) {
        const errorMsg = `[FATAL] Invalid BACKEND_URL in production: "${ENV_BACKEND_URL}" contains forbidden pattern. Production MUST use ${PRODUCTION_API_URL}`;
        console.error(errorMsg);
        // Don't throw - just force production URL
        return false;
      }
    }
  }
  return true;
}

// Run validation at module load
const configValid = validateProductionConfig();

// =============================================================================
// API_URL RESOLUTION - SINGLE SOURCE OF TRUTH
// =============================================================================
function resolveApiUrl() {
  // PRODUCTION: Always use hardcoded production URL
  if (IS_PRODUCTION) {
    console.info('[API] Production mode detected (NODE_ENV=production) → Using:', PRODUCTION_API_URL);
    return PRODUCTION_API_URL;
  }
  
  // DEVELOPMENT/TEST: Check hostname first
  const currentHost = typeof window !== 'undefined' ? window.location.host : '';
  
  // If running on production domain (even in dev mode), use production URL
  if (currentHost.includes(PRODUCTION_DOMAIN)) {
    console.info('[API] Production domain detected in hostname → Using:', PRODUCTION_API_URL);
    return PRODUCTION_API_URL;
  }
  
  // Use env var if provided (preview URLs are allowed in development)
  if (ENV_BACKEND_URL) {
    console.info('[API] Development mode → Using env URL:', ENV_BACKEND_URL);
    return ENV_BACKEND_URL;
  }
  
  // Fallback for local development
  const fallback = 'http://localhost:8001';
  console.info('[API] Development fallback → Using:', fallback);
  return fallback;
}

// =============================================================================
// EXPORTED API URL - THE ONLY ALLOWED BACKEND ENTRY POINT
// =============================================================================
export const API_URL = resolveApiUrl();
export const API = `${API_URL}/api`;

// Log at startup for debugging
console.info('[API] ══════════════════════════════════════════════════');
console.info('[API] Environment:', IS_PRODUCTION ? 'PRODUCTION' : 'DEVELOPMENT');
console.info('[API] API_URL:', API_URL);
console.info('[API] NODE_ENV:', process.env.NODE_ENV);
console.info('[API] Config Valid:', configValid);
console.info('[API] ══════════════════════════════════════════════════');

// =============================================================================
// P0 SAFARI iOS FIX: Global token cache
// Safari iOS has timing issues with localStorage - use a memory cache as backup
// =============================================================================
let _cachedToken = null;
let _tokenInitialized = false;

// Initialize from localStorage immediately (but don't rely solely on this)
try {
  _cachedToken = localStorage.getItem('token');
  if (_cachedToken) {
    _tokenInitialized = true;
  }
} catch (e) {
  console.warn('[API] Failed to read initial token from localStorage:', e);
}

// Function to get token with AGGRESSIVE fallback for Safari
const getAuthToken = () => {
  // If we have a cached token, use it
  if (_cachedToken) {
    return _cachedToken;
  }
  
  // ALWAYS try localStorage as fallback (Safari iOS fix)
  // This is critical because the initial read might have failed due to timing
  try {
    const token = localStorage.getItem('token');
    if (token) {
      _cachedToken = token; // Update cache for future calls
      _tokenInitialized = true;
      console.debug('[API] Token recovered from localStorage');
    }
    return token;
  } catch (e) {
    console.warn('[API] Failed to read token from localStorage:', e);
    return null;
  }
};

// Export function to update token from AuthContext
export const setAuthToken = (token) => {
  _cachedToken = token;
  if (token) {
    try {
      localStorage.setItem('token', token);
    } catch (e) {
      console.warn('[API] Failed to save token to localStorage:', e);
    }
  } else {
    try {
      localStorage.removeItem('token');
    } catch (e) {
      console.warn('[API] Failed to remove token from localStorage:', e);
    }
  }
};

// Export function to clear token
export const clearAuthToken = () => {
  _cachedToken = null;
  try {
    localStorage.removeItem('token');
  } catch (e) {
    console.warn('[API] Failed to clear token:', e);
  }
};

// =============================================================================
// AXIOS CLIENT WITH TRACING
// P0 iOS Safari DEFINITIVE FIX: Cookie fallback when all headers are stripped
// BUILD_MARKER: 20260214_p0_ios_cookie_auth_fallback_and_sw_bypass
// =============================================================================
export const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // P0 iOS Safari DEFINITIVE: Enable cookies for same-origin requests
  // This allows the HttpOnly ame_session cookie to be sent automatically
  // Works as fallback when Safari strips ALL request headers
  withCredentials: true,
});

// =============================================================================
// P0 DEFINITIVE FIX: Method validation configuration
// =============================================================================

/**
 * When true, invalid method calls (GET on POST-only routes) will throw
 * an error BEFORE the request is sent. This prevents 405 errors entirely.
 * 
 * Set to false for debugging (let backend return 405 with diagnostics).
 */
const STRICT_METHOD_ENFORCEMENT = true;

/**
 * P0 DEFINITIVE: Validate method before request is sent.
 * 
 * This function:
 * 1. Detects invalid methods on POST-only routes (GET, HEAD, PUT, PATCH, DELETE)
 * 2. Logs detailed violation info to console
 * 3. Optionally throws error to prevent the request (if STRICT_METHOD_ENFORCEMENT)
 * 
 * @param {Object} config - Axios request config
 * @returns {Object} config if valid
 * @throws {Error} if method violation and STRICT_METHOD_ENFORCEMENT is true
 */
function validateRequestMethod(config) {
  const url = config.url || '';
  const method = config.method || 'GET'; // axios defaults to GET if not specified
  
  // P0 DEFINITIVE: Check for method violation on ALL routes (not just Instagram)
  const violation = detectInvalidMethodOnRoute(url, method);
  
  if (violation) {
    // Always log the violation with full details + stack trace
    console.error(
      `\n[P0 405_PREVENTION] ❌ INVALID METHOD DETECTED`,
      `\n  Route: ${violation.matchedRoute}`,
      `\n  URL: ${violation.url}`,
      `\n  Method Used: ${violation.method}`,
      `\n  Expected: ${violation.expectedMethod}`,
      `\n  Fix: ${violation.fix}`,
      `\n  Timestamp: ${violation.timestamp}`,
      `\n  Stack Trace:`,
      new Error().stack
    );
    
    if (STRICT_METHOD_ENFORCEMENT) {
      // Throw error to prevent the request from being sent
      throw createMethodViolationError(violation);
    } else {
      // Warning only - let backend return 405 with diagnostics
      console.warn(
        `[P0 405_PREVENTION] ⚠️ Request will fail with 405. ` +
        `Set STRICT_METHOD_ENFORCEMENT=true to block at client level.`
      );
    }
  }
  
  return config;
}

// =============================================================================
// P0 DEFINITIVE: Safe API wrapper that enforces method
// =============================================================================

/**
 * P0 DEFINITIVE: Safe fetch wrapper that REQUIRES explicit method.
 * 
 * Unlike axios.get()/axios.post(), this function throws if method is not provided,
 * preventing accidental GET requests on POST-only routes.
 * 
 * @param {string} endpoint - API endpoint (without /api prefix)
 * @param {Object} options - Request options
 * @param {string} options.method - HTTP method (REQUIRED)
 * @param {Object} [options.data] - Request body (for POST/PUT/PATCH)
 * @param {Object} [options.params] - Query parameters
 * @param {Object} [options.headers] - Additional headers
 * @returns {Promise} Axios response promise
 * @throws {Error} If method is not specified for POST-only routes
 */
export async function apiFetch(endpoint, options = {}) {
  const { method, data, params, headers, ...rest } = options;
  
  // P0 DEFINITIVE: Require explicit method for POST-only routes
  if (!method && isPostOnlyRoute(endpoint)) {
    const error = new Error(
      `[apiFetch] Method is REQUIRED for POST-only route: ${endpoint}. ` +
      `Use apiFetch('${endpoint}', { method: 'POST', data: {...} })`
    );
    error.code = 'ERR_METHOD_REQUIRED';
    error.endpoint = endpoint;
    console.error(error.message, '\nStack:', new Error().stack);
    throw error;
  }
  
  // Default to GET only for non-POST-only routes
  const resolvedMethod = method || 'GET';
  
  const config = {
    url: endpoint,
    method: resolvedMethod,
    ...rest,
  };
  
  if (data) config.data = data;
  if (params) config.params = params;
  if (headers) config.headers = { ...config.headers, ...headers };
  
  return apiClient.request(config);
}

// =============================================================================
// REQUEST INTERCEPTOR - Auth & Tracing & P0 Method Validation
// P0 FIX iOS Safari: ALL token logic MUST be here, NOT in transformRequest
// =============================================================================
apiClient.interceptors.request.use(
  (config) => {
    // P0 FIX: Sanitize request data to prevent cyclic JSON.stringify errors
    // Axios internally calls JSON.stringify on config.data
    if (config.data && typeof config.data === 'object') {
      try {
        // Create a clean copy with only serializable data
        const cleanData = {};
        Object.keys(config.data).forEach(key => {
          const value = config.data[key];
          // Only include primitive types and simple objects
          if (
            value === null ||
            value === undefined ||
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean' ||
            Array.isArray(value) ||
            (typeof value === 'object' && value.constructor === Object)
          ) {
            cleanData[key] = value;
          }
          // Skip functions, DOM nodes, circular refs, etc.
        });
        config.data = cleanData;
      } catch (e) {
        console.warn('[API] Failed to sanitize request data:', e.message);
      }
    }
    
    // P0 DEFINITIVE: Validate method FIRST (may throw)
    validateRequestMethod(config);
    
    // P0 FIX iOS Safari LOGIN BUG: Force POST method for auth endpoints
    // iOS Safari has been observed converting POST to GET due to stale cache/SW
    const authEndpoints = ['/auth/login', '/auth/signup', '/auth/register', '/auth/logout'];
    const isAuthEndpoint = authEndpoints.some(ep => config.url?.includes(ep));
    
    if (isAuthEndpoint && config.method) {
      // Force uppercase method to ensure POST is correctly sent
      config.method = config.method.toUpperCase();
      console.info(`[API] Auth endpoint ${config.url} - Forcing method: ${config.method}`);
      
      // Add cache-busting headers for auth endpoints
      if (!config.headers) config.headers = {};
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
      config.headers['Expires'] = '0';
    }
    
    // P0 FIX: Cache-bust for sensitive endpoints (prevents stale iOS Safari data)
    // Only apply to GET requests for /projects and /integrations/x endpoints
    const isCacheSensitiveEndpoint = config.url && (
      config.url.includes('/projects') ||
      config.url.includes('/integrations/x/')
    );
    const isGetRequest = !config.method || config.method.toLowerCase() === 'get';
    
    if (isCacheSensitiveEndpoint && isGetRequest) {
      // Only add _ts if not already present
      if (!config.params) config.params = {};
      if (!config.params._ts) {
        config.params._ts = Date.now();
      }
    }
    
    // P0 FIX iOS Safari: Ensure headers object exists EARLY
    if (!config.headers) {
      config.headers = {};
    }
    
    // P0 CRITICAL FIX iOS Safari: Multi-attempt token retrieval
    // Safari iOS has race conditions where localStorage isn't ready immediately
    // We try multiple sources to ensure we get the token
    let token = null;
    let tokenSource = 'none';
    
    // Attempt 1: Memory cache (fastest, most reliable)
    token = getAuthToken();
    if (token) {
      tokenSource = 'cache';
    }
    
    // Attempt 2: Direct localStorage read (Safari iOS fallback)
    if (!token) {
      try {
        token = localStorage.getItem('token');
        if (token) {
          setAuthToken(token); // Sync to cache for future calls
          tokenSource = 'localStorage_direct';
          console.debug('[AUTH_DBG] Token recovered from direct localStorage read');
        }
      } catch (e) {
        console.warn('[AUTH_DBG] localStorage read failed:', e.message);
      }
    }
    
    // Attempt 3: sessionStorage fallback (some iOS browsers use this)
    if (!token) {
      try {
        token = sessionStorage.getItem('token');
        if (token) {
          setAuthToken(token); // Sync to primary storage
          tokenSource = 'sessionStorage';
          console.debug('[AUTH_DBG] Token recovered from sessionStorage');
        }
      } catch (e) {
        // Ignore
      }
    }
    
    // P0 PROD FIX: Explicit auth debugging (no secrets logged)
    const urlPath = config.url || '';
    const hasToken = !!token;
    
    // P0 iOS Safari DUAL-HEADER: Use debug mode via ?debug=1 URL param
    const debugMode = typeof window !== 'undefined' && window.location?.search?.includes('debug=1');
    
    if (debugMode) {
      console.info(`[AUTH_DBG] attaching_token=${hasToken} url=${urlPath} source=${tokenSource}`);
    }
    
    // P0 iOS Safari DUAL-HEADER FIX: Send BOTH headers to workaround Safari stripping Authorization
    // Backend will accept token from either header (Authorization primary, X-AME-Authorization fallback)
    if (token) {
      const bearerToken = `Bearer ${token}`;
      
      // Primary header: Authorization
      config.headers.Authorization = bearerToken;
      
      // Fallback header: X-AME-Authorization (iOS Safari workaround)
      config.headers['X-AME-Authorization'] = bearerToken;
      
      // P0 DEFINITIVE: Final verification log (only in debug mode, no token content)
      if (debugMode) {
        const authSet = config.headers.Authorization?.startsWith('Bearer ');
        const xAmeSet = config.headers['X-AME-Authorization']?.startsWith('Bearer ');
        console.info(`[AUTH_DBG] ✅ DUAL-HEADER set: Authorization=${authSet}, X-AME-Authorization=${xAmeSet}`);
      }
    } else {
      // Only warn for authenticated routes (not login/register/signup)
      const isPublicRoute = config.url?.includes('/auth/login') || 
                           config.url?.includes('/auth/register') ||
                           config.url?.includes('/auth/signup') ||
                           config.url?.includes('/auth/google') ||
                           config.url?.includes('/health') ||
                           config.url?.includes('/auth/echo') ||
                           config.url?.includes('/version');
      if (!isPublicRoute && debugMode) {
        console.warn(`[AUTH_DBG] ⚠️ No token for protected route: ${config.url}`);
      }
    }
    
    // Add request ID for tracing
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    config.headers['X-Request-Id'] = requestId;
    
    // P0 HARDENING: Generate and add correlation ID for request chain tracing
    const correlationId = config.headers['X-Correlation-Id'] || 
                         sessionStorage.getItem('correlationId') ||
                         `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    config.headers['X-Correlation-Id'] = correlationId;
    
    // Store correlation ID in session for chained requests
    sessionStorage.setItem('correlationId', correlationId);
    
    config.metadata = { requestId, correlationId, startTime: Date.now() };
    
    // Log request for debugging
    const method = config.method?.toUpperCase() || 'GET';
    console.debug(`[API Request] ${method} ${config.baseURL}${config.url} [corr:${correlationId.substring(0,8)}]`);
    
    // P0 DIAGNOSTIC: Log detailed info for POST-only routes
    if (isPostOnlyRoute(config.url)) {
      console.info(
        `[P0 POST_ONLY_ROUTE] ` +
        `\n  URL: ${config.url}` +
        `\n  Method: ${method}` +
        `\n  Expected: POST` +
        `\n  Body Present: ${!!config.data}` +
        `\n  Content-Type: ${config.headers?.['Content-Type'] || 'not set'}` +
        `\n  Request ID: ${requestId}`
      );
      
      // Ensure method is POST for POST-only routes
      if (method !== 'POST' && method !== 'OPTIONS') {
        console.error(
          `[P0 405_PREVENTION] ❌ CRITICAL: Non-POST request to POST-only route!` +
          `\n  This should have been caught by validateRequestMethod.` +
          `\n  URL: ${config.url}` +
          `\n  Method: ${method}` +
          `\n  Stack:`, new Error().stack
        );
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// =============================================================================
// RESPONSE INTERCEPTOR - Error Handling & Request ID
// =============================================================================
apiClient.interceptors.response.use(
  (response) => {
    const requestId = response.headers['x-request-id'] || response.config.metadata?.requestId;
    const duration = response.config.metadata ? Date.now() - response.config.metadata.startTime : 0;
    console.debug(`[API Response] ${response.status} (${duration}ms) [${requestId}]`);
    return response;
  },
  (error) => {
    const requestId = error.response?.headers?.['x-request-id'] || 
                      error.config?.metadata?.requestId || 
                      `req_client_${Date.now()}`;
    const correlationId = error.response?.headers?.['x-correlation-id'] ||
                          error.config?.metadata?.correlationId ||
                          `corr_client_${Date.now()}`;
    
    if (error.response) {
      const { status, data } = error.response;
      
      // P0 POST CHANNEL: Special handling for 405 Method Not Allowed
      // L'intercepteur enrichit l'erreur mais ne l'avale PAS
      // La logique d'affichage est dans handleActionError
      if (status === 405) {
        const debugInfo = data?.debug_info || {};
        const method = debugInfo?.method || error.config?.method?.toUpperCase() || 'GET';
        const suspect = debugInfo?.suspect;
        
        console.info(
          `[405_INTERCEPTOR] method=${method} suspect=${suspect} ` +
          `path=${debugInfo.path || error.config?.url}`
        );
        
        // Enrichir l'erreur avec les infos utiles pour handleActionError
        error.errorInfo = {
          error: 'HTTP_405',
          error_code: 'HTTP_405',
          message: data?.message || `Méthode ${method} non autorisée`,
          stage: data?.stage || 'MIDDLEWARE_PROTECTION',
          request_id: data?.request_id || requestId,
          correlation_id: correlationId,
          is_actionable: data?.is_actionable ?? true,
          transient_hint: data?.transient_hint ?? false,
          debug_info: debugInfo,
          timestamp: data?.timestamp || new Date().toISOString(),
          status: 405,
          is405Error: true,
          suspect,
          method
        };
        
        // NE PAS avaler - laisser handleActionError décider
        return Promise.reject(error);
      }
      
      // P0 PROD FIX: Handle 401 - Trigger reauth event for all endpoints
      if (status === 401) {
        const errorCode = data?.error_code || data?.error;
        const reason = data?.reason || 'unknown';
        const requestUrl = error.config?.url || '';
        
        // P0 PROD FIX: Log 401 with action
        console.warn(`[AUTH_DBG] 401 url=${requestUrl}, error_code=${errorCode}, reason=${reason}`);
        
        // P0 PROD FIX: Dispatch reauth event for REAUTH_REQUIRED errors
        if (errorCode === 'REAUTH_REQUIRED') {
          console.warn('[AUTH_DBG] REAUTH_REQUIRED detected - dispatching event');
          dispatchReauthRequired(reason);
        }
        
        error.errorInfo = {
          error: data?.error || 'UNAUTHORIZED',
          error_code: errorCode || 'REAUTH_REQUIRED',
          message: data?.message || 'Authentification requise',
          reason,
          request_id: requestId,
          status: 401,
          isReauthRequired: errorCode === 'REAUTH_REQUIRED'
        };
        
        return Promise.reject(error);
      }
      
      // P0 LOCKDOWN: Check if response is a structured ErrorEnvelope
      const isStructuredEnvelope = data && (data.error_code || data.stage || data.next_steps);
      
      // P0 LOCKDOWN: Check if response is HTML (server error page)
      const isHtmlResponse = typeof data === 'string' && 
                              (data.includes('<html') || data.includes('<!DOCTYPE'));
      
      if (isStructuredEnvelope) {
        // Route-level ErrorEnvelope - preserve it
        error.errorInfo = {
          // Core error info
          error: data.error || data.error_code,
          error_code: data.error_code || data.error,
          message: data.message || getDefaultErrorMessage(status),
          
          // P0 LOCKDOWN: Include full ErrorEnvelope fields
          stage: data.stage || 'UNKNOWN',
          request_id: data.request_id || requestId,
          correlation_id: data.correlation_id || correlationId,
          next_steps: data.next_steps || [],
          debug_info: data.debug_info || {},
          timestamp: data.timestamp,
          
          // Legacy fields for compatibility
          requestId: data.request_id || requestId,
          status,
          nextAction: data.next_action || getNextAction(status),
          
          // P0 LOCKDOWN: Flag to indicate this is a structured ErrorEnvelope
          isErrorEnvelope: true,
        };
        
        console.error(`[API Error] ${status} [${error.errorInfo.error_code}] stage=${error.errorInfo.stage} req=${error.errorInfo.request_id}`, error.errorInfo.message);
      } else if (status >= 500 || isHtmlResponse) {
        // P0 LOCKDOWN: Create synthetic envelope for non-envelope 500 errors
        // This ensures UI always has structured error to display
        error.errorInfo = {
          error: 'INTERNAL_ERROR',
          error_code: 'INTERNAL_ERROR',
          message: isHtmlResponse 
            ? 'Erreur serveur (réponse HTML reçue au lieu de JSON)'
            : (data?.message || data?.detail || getDefaultErrorMessage(status)),
          
          // P0 LOCKDOWN: Use CLIENT_FALLBACK stage to indicate synthetic envelope
          stage: 'CLIENT_FALLBACK',
          request_id: requestId,
          correlation_id: correlationId,
          next_steps: [
            'Réessayez dans quelques instants',
            'Si le problème persiste, copiez le Debug Bundle pour le support'
          ],
          debug_info: {
            original_status: status,
            was_html_response: isHtmlResponse,
            synthetic_envelope: true
          },
          timestamp: new Date().toISOString(),
          
          // Legacy fields
          requestId,
          status,
          nextAction: 'RETRY',
          
          // P0 LOCKDOWN: Flag to indicate this is a synthetic envelope
          isErrorEnvelope: true,
          isSyntheticEnvelope: true,
        };
        
        console.error(`[API Error] ${status} [SYNTHETIC] stage=CLIENT_FALLBACK req=${requestId}`, error.errorInfo.message);
      } else {
        // 4xx errors without envelope structure
        error.errorInfo = {
          error: data?.error || `HTTP_${status}`,
          error_code: data?.error || `HTTP_${status}`,
          message: data?.message || data?.detail || getDefaultErrorMessage(status),
          stage: 'UNKNOWN',
          request_id: requestId,
          correlation_id: correlationId,
          next_steps: [],
          debug_info: {},
          requestId,
          status,
          nextAction: getNextAction(status),
          isErrorEnvelope: false,
        };
        
        console.error(`[API Error] ${status} [${requestId}] (non-envelope response)`, error.errorInfo);
      }
    } else {
      // Network error (no response)
      // P0 FIX: Safely extract error message without triggering cyclic stringify
      let networkErrorMsg = 'Erreur réseau';
      try {
        if (error.message && typeof error.message === 'string') {
          networkErrorMsg = error.message;
        }
      } catch {
        networkErrorMsg = 'Erreur réseau inconnue';
      }
      
      error.errorInfo = {
        error: 'NETWORK_ERROR',
        error_code: 'NETWORK_ERROR',
        message: 'Erreur réseau. Vérifiez votre connexion.',
        stage: 'NETWORK',
        request_id: requestId,
        correlation_id: correlationId,
        next_steps: ['Vérifiez votre connexion internet', 'Réessayez dans quelques instants'],
        requestId,
        isErrorEnvelope: true,  // Treat as envelope for consistent handling
        isSyntheticEnvelope: true,
      };
      // P0 FIX: Log only the safe message, not the error object
      console.error(`[API Network Error] [${requestId}] ${networkErrorMsg}`);
    }
    
    return Promise.reject(error);
  }
);

function getDefaultErrorMessage(status) {
  const messages = {
    401: 'Authentification requise',
    403: 'Accès non autorisé',
    404: 'Ressource non trouvée',
    429: 'Trop de requêtes. Réessayez plus tard.',
    500: 'Erreur serveur. Réessayez plus tard.',
  };
  return messages[status] || 'Une erreur est survenue';
}

function getNextAction(status) {
  if (status === 401 || status === 403) return 'LOGIN';
  if (status === 429) return 'RETRY_LATER';
  return null;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================
export function formatApiError(error) {
  if (error.errorInfo) {
    return {
      message: error.errorInfo.message,
      requestId: error.errorInfo.requestId,
      nextAction: error.errorInfo.nextAction,
    };
  }
  return { message: error.message || 'Une erreur est survenue', requestId: 'unknown' };
}

export function isAuthError(error) {
  return error.response?.status === 401 || 
         error.response?.status === 403 ||
         error.errorInfo?.nextAction === 'LOGIN';
}

// =============================================================================
// ENVIRONMENT INFO FOR DEBUGGING
// =============================================================================
export function getEnvironmentInfo() {
  return {
    apiUrl: API_URL,
    isProduction: IS_PRODUCTION,
    nodeEnv: process.env.NODE_ENV,
    currentHost: typeof window !== 'undefined' ? window.location.host : 'server',
    configValid,
  };
}

export const isValidEnvironment = () => configValid;

// =============================================================================
// P0 HARDENING: PROJECT-AWARE API UTILITIES
// =============================================================================

// =============================================================================
// P0 PRODUCTION-GRADE: PROJECT ID NORMALIZATION
// =============================================================================

/**
 * P0 HARDENING: Normalizes project ID - rejects empty/whitespace strings.
 * MUST be used before ANY project-scoped API call.
 * 
 * @param {string|null|undefined} projectId - Raw project ID
 * @returns {string|null} Normalized project ID or null if invalid
 */
export function normalizeProjectId(projectId) {
  if (projectId === null || projectId === undefined) {
    return null;
  }
  
  if (typeof projectId !== 'string') {
    return null;
  }
  
  const trimmed = projectId.trim();
  
  // Empty string or whitespace-only = null (invalid)
  if (trimmed === '' || /^\s*$/.test(trimmed)) {
    return null;
  }
  
  return trimmed;
}

/**
 * P0 HARDENING: Creates project context headers.
 * THROWS ERROR if projectId is missing/empty - callers MUST handle.
 * 
 * @param {string} projectId - Project ID (REQUIRED, will be validated)
 * @returns {Object} Headers with X-Project-Id and X-Correlation-Id
 * @throws {Error} If projectId is missing/empty
 */
export function createProjectHeaders(projectId) {
  // P0 HARDENING: Normalize and validate
  const normalizedId = normalizeProjectId(projectId);
  
  if (!normalizedId) {
    const error = new Error('PROJECT_ID_REQUIRED');
    error.errorCode = 'PROJECT_ID_REQUIRED';
    error.stage = 'CONTEXT_RESOLVE';
    error.message = 'Un projet doit être sélectionné pour cette opération';
    console.error('[API] ❌ PROJECT_ID_REQUIRED: createProjectHeaders called without valid projectId');
    throw error;
  }
  
  const correlationId = sessionStorage.getItem('correlationId') || 
                       `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  return {
    'X-Correlation-Id': correlationId,
    'X-Project-Id': normalizedId
  };
}

/**
 * P0 HARDENING (SAFE VERSION): Creates project headers without throwing.
 * Returns headers with or without X-Project-Id based on availability.
 * USE ONLY for diagnostic/optional project context calls.
 * 
 * @param {string|null} projectId - Project ID (optional)
 * @returns {Object} Headers object
 */
export function createProjectHeadersSafe(projectId) {
  const correlationId = sessionStorage.getItem('correlationId') || 
                       `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const headers = {
    'X-Correlation-Id': correlationId,
  };
  
  const normalizedId = normalizeProjectId(projectId);
  if (normalizedId) {
    headers['X-Project-Id'] = normalizedId;
  }
  
  return headers;
}

/**
 * P0 HARDENING: Validates that projectId is present before API call.
 * Shows user-friendly toast error if missing.
 * 
 * @param {string} projectId - Project ID to validate
 * @param {string} operation - Description of the operation (for error message)
 * @returns {boolean} True if valid, false if missing
 */
export function validateProjectIdRequired(projectId, operation = 'cette opération') {
  if (!projectId) {
    const errorMsg = `❌ PROJECT_ID_REQUIRED: Un projet doit être sélectionné pour ${operation}`;
    console.error(`[API] ${errorMsg}`);
    // Note: Toast should be imported in the component using this function
    return false;
  }
  return true;
}

// =============================================================================
// P0 FINAL: PROJECT-SCOPED ROUTES REGISTRY
// =============================================================================

/**
 * P0 FINAL: Routes that MUST be called with projectId.
 * If a route is called without projectId, it's a frontend bug.
 */
const PROJECT_SCOPED_ROUTES = [
  // Social integrations
  '/social/instagram/',
  '/social/facebook/',
  '/social/x/',
  '/social/linkedin/',
  
  // Integrations status
  '/integrations/telegram/',
  '/integrations/x/',
  '/integrations/facebook/',
  
  // Scheduler
  '/scheduler/',
  
  // Contents
  '/contents/',
  
  // Projects (except create)
  '/projects/',
  
  // AI
  '/ai/generate',
  '/ai/suggest',
  
  // Audit
  '/audit/',
];

/**
 * P0 FINAL: Routes that do NOT require projectId.
 */
const NON_PROJECT_ROUTES = [
  '/auth/',
  '/debug/whoami',
  '/debug/env',
  '/version',
  '/health',
  '/callback',
];

/**
 * P0 FINAL: Guard that throws if a project-scoped route is called without projectId.
 * 
 * @param {string} endpoint - API endpoint
 * @param {string} projectId - Project ID (may be null)
 * @throws {Error} If route requires projectId but none provided
 */
export function guardProjectScopedRoute(endpoint, projectId) {
  // Normalize endpoint
  const normalizedEndpoint = endpoint.startsWith('/api') 
    ? endpoint.slice(4) 
    : endpoint;
  
  // Check if route is project-scoped
  const isProjectScoped = PROJECT_SCOPED_ROUTES.some(
    route => normalizedEndpoint.startsWith(route) || normalizedEndpoint.includes(route)
  );
  
  // Check if route is explicitly non-project
  const isNonProject = NON_PROJECT_ROUTES.some(
    route => normalizedEndpoint.startsWith(route) || normalizedEndpoint.includes(route)
  );
  
  // P0 FINAL: If project-scoped and no projectId, log violation
  if (isProjectScoped && !isNonProject && !projectId) {
    const violation = {
      type: 'FRONTEND_CONTEXT_VIOLATION',
      endpoint: normalizedEndpoint,
      timestamp: new Date().toISOString(),
      stack: new Error().stack
    };
    
    console.error(
      `[P0_GUARD] ❌ FRONTEND_CONTEXT_VIOLATION: ` +
      `Project-scoped route "${normalizedEndpoint}" called without projectId\n` +
      `This is a frontend bug. Use projectGet/projectPost with a valid projectId.`
    );
    
    // Log to console for debugging
    console.error('[P0_GUARD] Violation details:', violation);
    
    // In strict mode, throw to prevent the call
    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        `P0_GUARD: Route "${normalizedEndpoint}" requires projectId. ` +
        `Use projectGet/projectPost with a valid projectId.`
      );
    }
    
    return false;
  }
  
  return true;
}

/**
 * P0 HARDENING: Makes API POST request with automatic project context injection.
 * Validates projectId before sending and includes it in both header AND body.
 * 
 * @param {string} endpoint - API endpoint (without /api prefix)
 * @param {Object} data - Request body
 * @param {string} projectId - Project ID (REQUIRED)
 * @param {Object} options - Additional axios options
 * @returns {Promise} Axios response promise
 */
export async function projectPost(endpoint, data, projectId, options = {}) {
  // P0 FINAL: Guard against project-scoped routes without projectId
  guardProjectScopedRoute(endpoint, projectId);
  
  // P0 HARDENING: Normalize and validate projectId
  const normalizedId = normalizeProjectId(projectId);
  
  if (!normalizedId) {
    const error = new Error('PROJECT_ID_REQUIRED');
    error.errorCode = 'PROJECT_ID_REQUIRED';
    error.stage = 'CONTEXT_RESOLVE';
    error.errorInfo = {
      error_code: 'PROJECT_ID_REQUIRED',
      stage: 'CONTEXT_RESOLVE',
      message: 'Un projet doit être sélectionné pour cette opération',
      request_id: 'local_validation',
      next_steps: ['Sélectionnez un projet', 'Retournez au tableau de bord']
    };
    console.error('[API] ❌ projectPost rejected: PROJECT_ID_REQUIRED');
    return Promise.reject(error);
  }
  
  const headers = {
    ...createProjectHeaders(normalizedId),
    ...(options.headers || {})
  };
  
  // P0: Include project_id in body as well for strict resolver
  const bodyWithProject = {
    ...data,
    project_id: normalizedId
  };
  
  return apiClient.post(endpoint, bodyWithProject, {
    ...options,
    headers
  });
}

/**
 * P0 HARDENING: Makes API GET request with automatic project context injection.
 * 
 * @param {string} endpoint - API endpoint (without /api prefix)
 * @param {string} projectId - Project ID (REQUIRED)
 * @param {Object} params - Query parameters
 * @param {Object} options - Additional axios options
 * @returns {Promise} Axios response promise
 */
export async function projectGet(endpoint, projectId, params = {}, options = {}) {
  // P0 FINAL: Guard against project-scoped routes without projectId
  guardProjectScopedRoute(endpoint, projectId);
  
  // P0 HARDENING: Normalize and validate projectId
  const normalizedId = normalizeProjectId(projectId);
  
  if (!normalizedId) {
    const error = new Error('PROJECT_ID_REQUIRED');
    error.errorCode = 'PROJECT_ID_REQUIRED';
    error.stage = 'CONTEXT_RESOLVE';
    error.errorInfo = {
      error_code: 'PROJECT_ID_REQUIRED',
      stage: 'CONTEXT_RESOLVE',
      message: 'Un projet doit être sélectionné pour cette opération',
      request_id: 'local_validation',
      next_steps: ['Sélectionnez un projet', 'Retournez au tableau de bord']
    };
    console.error('[API] ❌ projectGet rejected: PROJECT_ID_REQUIRED');
    return Promise.reject(error);
  }
  
  const headers = {
    ...createProjectHeaders(normalizedId),
    ...(options.headers || {})
  };
  
  return apiClient.get(endpoint, {
    ...options,
    headers,
    params: {
      ...params,
      project_id: normalizedId
    }
  });
}

/**
 * P0 DEFINITIVE: Makes API GET request with AME auth but WITHOUT project context.
 * 
 * Use this for endpoints that require AME JWT but don't need project_id:
 * - /integrations/x/status
 * - /integrations/telegram/status
 * - /integrations/callback-urls
 * - etc.
 * 
 * The token is automatically injected by the axios interceptor.
 * This wrapper ensures consistent error handling and logging.
 * 
 * @param {string} endpoint - API endpoint (without /api prefix)
 * @param {Object} params - Query parameters
 * @param {Object} options - Additional axios options
 * @returns {Promise} Axios response promise
 */
export async function authGet(endpoint, params = {}, options = {}) {
  // Generate correlation ID for tracing
  const correlationId = `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const headers = {
    'X-Correlation-Id': correlationId,
    ...(options.headers || {})
  };
  
  return apiClient.get(endpoint, {
    ...options,
    headers,
    params
  });
}

/**
 * P0 DEFINITIVE: Makes API POST request with AME auth but WITHOUT project context.
 * 
 * Use this for endpoints that require AME JWT but don't need project_id.
 * 
 * @param {string} endpoint - API endpoint (without /api prefix)
 * @param {Object} data - Request body
 * @param {Object} options - Additional axios options
 * @returns {Promise} Axios response promise
 */
export async function authPost(endpoint, data = {}, options = {}) {
  const correlationId = `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const headers = {
    'X-Correlation-Id': correlationId,
    ...(options.headers || {})
  };
  
  return apiClient.post(endpoint, data, {
    ...options,
    headers
  });
}

// =============================================================================
// P0 HARDENING: ERROR CODE HANDLERS
// =============================================================================

/**
 * P0 PRODUCTION-GRADE: Error codes for scope authority invariants
 */
export const SCOPE_AUTHORITY_ERROR_CODES = {
  PROJECT_ID_REQUIRED: 'PROJECT_ID_REQUIRED',
  PROJECT_ID_MISMATCH: 'PROJECT_ID_MISMATCH',
  SCOPE_AUTHORITY_INVALID: 'SCOPE_AUTHORITY_INVALID',
  SCOPE_SOURCE_UNAVAILABLE: 'SCOPE_SOURCE_UNAVAILABLE',
  MISSING_PERMISSIONS: 'MISSING_PERMISSIONS',
  PROJECT_CONTEXT_MISSING: 'PROJECT_CONTEXT_MISSING'
};

/**
 * P0 FIX: Error codes for Meta OAuth errors (error 190, etc.)
 */
export const META_OAUTH_ERROR_CODES = {
  META_TOKEN_INVALID: 'META_TOKEN_INVALID',
  META_APP_ID_MISMATCH: 'META_APP_ID_MISMATCH',
  META_TOKEN_EXPIRED: 'META_TOKEN_EXPIRED',
  META_PASSWORD_CHANGED: 'META_PASSWORD_CHANGED',
  META_SESSION_INVALID: 'META_SESSION_INVALID'
};

/**
 * P0 DEFINITIVE: AME Auth error codes (JWT issues, not Meta)
 * These errors require AME login/logout, NOT Meta reconnect
 */
export const AME_AUTH_ERROR_CODES = {
  INVALID_TOKEN: 'INVALID_TOKEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  MISSING_TOKEN: 'MISSING_TOKEN',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  AME_JWT_REQUIRED: 'AME_JWT_REQUIRED',
  NOT_AUTHENTICATED: 'NOT_AUTHENTICATED'
};

/**
 * P0 DEFINITIVE: Check if error is an AME Auth error (not Meta error)
 * 
 * CRITICAL: AME auth errors (401/403 with AME error codes) should NEVER 
 * display "no_scopes_granted" or "Meta OAuth not connected". They should
 * display "Session expirée / Non authentifié" with login CTA.
 * 
 * @param {Error|Object} error - The error object
 * @returns {Object|null} Error info if AME auth error, null otherwise
 */
export function isAmeAuthError(error) {
  const status = error?.response?.status;
  const errorCode = error?.response?.data?.error_code || 
                   error?.response?.data?.error ||
                   error?.response?.data?.detail?.error_code ||
                   error?.errorInfo?.error_code;
  
  // HTTP 401/403 with no specific error code => AME auth error
  if ((status === 401 || status === 403) && !errorCode) {
    return {
      errorCode: status === 401 ? 'UNAUTHORIZED' : 'FORBIDDEN',
      httpStatus: status,
      message: 'Session AME expirée ou non authentifiée. Reconnectez-vous.',
      stage: 'AUTH',
      requiresAmeLogin: true,
      ctaAction: 'AME_LOGIN',
      ctaLabel: 'Se reconnecter'
    };
  }
  
  // Specific AME auth error codes
  const AME_CODES = Object.values(AME_AUTH_ERROR_CODES);
  if (AME_CODES.includes(errorCode)) {
    return {
      errorCode,
      httpStatus: status,
      message: error?.response?.data?.message || 
               error?.response?.data?.detail?.message || 
               'Session AME expirée. Reconnectez-vous.',
      stage: error?.response?.data?.stage || 'AUTH',
      requestId: error?.response?.data?.request_id,
      requiresAmeLogin: true,
      ctaAction: 'AME_LOGIN',
      ctaLabel: 'Se reconnecter'
    };
  }
  
  return null;
}

/**
 * P0 DEFINITIVE: Check if error is a network/timeout error
 * These errors should NOT display "no_scopes_granted"
 * 
 * @param {Error} error - The error object
 * @returns {Object|null} Error info if network error, null otherwise
 */
export function isNetworkError(error) {
  // Axios network error (no response)
  if (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK') {
    return {
      errorCode: 'NETWORK_ERROR',
      message: 'Erreur réseau. Vérifiez votre connexion.',
      isTimeout: error?.code === 'ECONNABORTED',
      ctaAction: 'RETRY',
      ctaLabel: 'Réessayer'
    };
  }
  
  // No response at all
  if (error?.request && !error?.response) {
    return {
      errorCode: 'NO_RESPONSE',
      message: 'Serveur inaccessible. Réessayez plus tard.',
      isTimeout: true,
      ctaAction: 'RETRY',
      ctaLabel: 'Réessayer'
    };
  }
  
  return null;
}

/**
 * P0 DEFINITIVE: Classify an error into the correct category
 * 
 * Priority order:
 * 1. AME Auth error (401/403 with AME codes) => "Session expirée"
 * 2. Network error (timeout/no response) => "Erreur réseau"
 * 3. Meta OAuth error (Meta error codes) => "Reconnectez Instagram"
 * 4. Meta API unavailable => "Meta temporairement indisponible"
 * 5. Other => Original error message
 * 
 * CRITICAL: NEVER show "no_scopes_granted" for categories 1-2-4
 * 
 * @param {Error} error - The error object
 * @returns {Object} Classified error with type and display info
 */
export function classifyApiError(error) {
  // 1. Check AME auth error first
  const ameError = isAmeAuthError(error);
  if (ameError) {
    return {
      type: 'AME_AUTH',
      ...ameError,
      showNoScopes: false,  // NEVER show no_scopes for AME auth errors
      showMetaReconnect: false
    };
  }
  
  // 2. Check network error
  const networkError = isNetworkError(error);
  if (networkError) {
    return {
      type: 'NETWORK',
      ...networkError,
      showNoScopes: false,  // NEVER show no_scopes for network errors
      showMetaReconnect: false
    };
  }
  
  // 3. Check Meta OAuth error
  const metaError = isMetaOAuthError(error);
  if (metaError) {
    return {
      type: 'META_OAUTH',
      ...metaError,
      showNoScopes: false,  // Meta OAuth error has specific handling
      showMetaReconnect: true
    };
  }
  
  // 4. Check if Meta API unavailable (scope source unavailable)
  const contextError = isContextIntegrityError(error);
  if (contextError?.errorCode === 'SCOPE_SOURCE_UNAVAILABLE') {
    return {
      type: 'META_UNAVAILABLE',
      ...contextError,
      message: 'Meta API temporairement indisponible. Réessayez plus tard.',
      showNoScopes: false,  // NEVER show no_scopes for Meta unavailable
      showMetaReconnect: false,
      ctaAction: 'RETRY',
      ctaLabel: 'Réessayer'
    };
  }
  
  // 5. Other errors - iter 247 P0: preserve the full structured error,
  // never collapse a backend-provided message into a blind default.
  // Priority: data.message -> data.error.message -> data.detail.message
  // -> error.message -> 'Erreur inconnue' only as an absolute last resort.
  const _data = error?.response?.data;
  const _message = _data?.message
    || _data?.error?.message
    || _data?.detail?.message
    || (typeof _data?.detail === 'string' ? _data.detail : null)
    || error?.message
    || 'Erreur inconnue';
  return {
    type: 'OTHER',
    errorCode: _data?.error_code || _data?.error?.code || 'UNKNOWN',
    message: _message,
    stage: _data?.stage,
    traceId: _data?.trace_id,
    pinterestStatus: _data?.pinterest_status,
    pinterestMessage: _data?.pinterest_message,
    httpStatus: error?.response?.status,
    showNoScopes: true,  // Only "other" errors can potentially show no_scopes
    showMetaReconnect: false
  };
}

/**
 * P0 FIX: Check if error is a Meta OAuth error (requires reconnect)
 * @param {Error} error - The error object
 * @returns {Object|null} Error info if Meta OAuth error, null otherwise
 */
export function isMetaOAuthError(error) {
  const errorCode = error.response?.data?.error_code || 
                   error.response?.data?.detail?.error_code ||
                   error.errorInfo?.error_code;
  
  const META_ERROR_CODES = Object.values(META_OAUTH_ERROR_CODES);
  
  if (META_ERROR_CODES.includes(errorCode)) {
    const debugInfo = error.response?.data?.debug_info || 
                     error.response?.data?.detail?.debug_info ||
                     error.errorInfo?.debug_info || {};
    
    return {
      errorCode,
      message: error.response?.data?.message || error.response?.data?.detail?.message || error.errorInfo?.message,
      stage: error.response?.data?.stage || error.response?.data?.detail?.stage || error.errorInfo?.stage,
      requestId: error.response?.data?.request_id || error.response?.data?.detail?.request_id || error.errorInfo?.request_id,
      nextSteps: error.response?.data?.next_steps || error.response?.data?.detail?.next_steps || error.errorInfo?.next_steps || [],
      debugInfo,
      metaErrorCode: debugInfo.meta_error_code,
      metaErrorSubcode: debugInfo.meta_error_subcode,
      metaErrorMessage: debugInfo.meta_error_message,
      requiresReconnect: true,  // P0 FIX: All Meta OAuth errors require reconnect
      ctaAction: 'RECONNECT_INSTAGRAM'
    };
  }
  
  return null;
}

/**
 * Checks if error is a P0 context integrity error.
 * @param {Error} error - The error object
 * @returns {Object|null} Error info if P0 error, null otherwise
 */
export function isContextIntegrityError(error) {
  const errorCode = error.response?.data?.error_code || 
                   error.response?.data?.detail?.error_code;
  
  const P0_ERROR_CODES = Object.values(SCOPE_AUTHORITY_ERROR_CODES);
  
  if (P0_ERROR_CODES.includes(errorCode)) {
    return {
      errorCode,
      message: error.response?.data?.message || error.response?.data?.detail?.message,
      stage: error.response?.data?.stage || error.response?.data?.detail?.stage,
      requestId: error.response?.data?.request_id || error.response?.data?.detail?.request_id,
      nextSteps: error.response?.data?.next_steps || error.response?.data?.detail?.next_steps || [],
      debugInfo: error.response?.data?.debug_info || error.response?.data?.detail?.debug_info,
      scopeResolution: error.response?.data?.debug_info?.scope_resolution || 
                      error.response?.data?.detail?.debug_info?.scope_resolution
    };
  }
  
  return null;
}

/**
 * P0 PRODUCTION-GRADE: Formats scope authority error for user display.
 * Returns structured error info with error_code, stage, request_id.
 * NEVER returns legacy "permission manquante" if error_code exists.
 * 
 * @param {Error} error - The error object
 * @returns {Object} Structured error with message, errorCode, ctas
 */
export function formatScopeAuthorityError(error) {
  const detail = error.response?.data?.detail || error.response?.data || {};
  const errorCode = detail.error_code;
  const stage = detail.stage || 'UNKNOWN';
  const requestId = detail.request_id || 'N/A';
  const message = detail.message || 'Erreur inconnue';
  const nextSteps = detail.next_steps || [];
  const scopeResolution = detail.debug_info?.scope_resolution;
  
  // P0 GUARANTEE: NEVER show generic "permission manquante" when error_code exists
  const result = {
    errorCode,
    stage,
    requestId,
    message,
    nextSteps,
    scopeResolution,
    isMetaApiUnavailable: false,
    ctas: []
  };
  
  switch (errorCode) {
    case SCOPE_AUTHORITY_ERROR_CODES.SCOPE_SOURCE_UNAVAILABLE:
      result.message = message || "L'API Meta est inaccessible";
      result.isMetaApiUnavailable = true;
      result.ctas = [
        { label: 'Réessayer', action: 'RETRY' },
        { label: 'Désactiver proxy/WAF', action: 'DISABLE_PROXY' },
        { label: 'Reconnect (rerequest)', action: 'RECONNECT' }
      ];
      break;
      
    case SCOPE_AUTHORITY_ERROR_CODES.SCOPE_AUTHORITY_INVALID:
      result.message = message || "Token de type PAGE détecté";
      result.ctas = [
        { label: 'Reconnect (token USER requis)', action: 'RECONNECT' }
      ];
      break;
      
    case SCOPE_AUTHORITY_ERROR_CODES.PROJECT_ID_REQUIRED:
      result.message = message || "Sélectionnez un projet";
      result.ctas = [
        { label: 'Sélectionner un projet', action: 'SELECT_PROJECT' }
      ];
      break;
      
    case SCOPE_AUTHORITY_ERROR_CODES.MISSING_PERMISSIONS:
      result.message = message || "Permissions manquantes";
      result.ctas = [
        { label: 'Reconnect (rerequest)', action: 'RECONNECT' }
      ];
      break;
      
    default:
      result.ctas = nextSteps.map(step => ({ label: step, action: 'INFO' }));
  }
  
  return result;
}

/**
 * Formats P0 error for user display (simple string version).
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export function formatP0Error(error) {
  const p0Error = isContextIntegrityError(error);
  
  if (p0Error) {
    const messages = {
      'PROJECT_ID_REQUIRED': '⚠️ Sélectionnez un projet avant de continuer',
      'PROJECT_ID_MISMATCH': '⚠️ Conflit de projet détecté. Rechargez la page.',
      'SCOPE_AUTHORITY_INVALID': '⚠️ Token de type PAGE. Reconnectez avec un token USER.',
      'SCOPE_SOURCE_UNAVAILABLE': '⚠️ API Meta inaccessible. Réessayez ou désactivez proxy/WAF.',
      'MISSING_PERMISSIONS': '⚠️ Permissions manquantes. Reconnectez Instagram.',
      'PROJECT_CONTEXT_MISSING': '⚠️ Contexte projet manquant'
    };
    return messages[p0Error.errorCode] || p0Error.message || 'Erreur de contexte';
  }
  
  // P0 FIX: Check for Meta OAuth error (error 190, etc.)
  const metaError = isMetaOAuthError(error);
  if (metaError) {
    const metaMessages = {
      'META_TOKEN_INVALID': '🔑 Token Meta invalide. Reconnexion obligatoire.',
      'META_APP_ID_MISMATCH': '🔑 Token généré par une autre app. Reconnectez Instagram.',
      'META_TOKEN_EXPIRED': '🔑 Token Meta expiré. Reconnectez Instagram.',
      'META_PASSWORD_CHANGED': '🔑 Mot de passe Facebook modifié. Reconnectez Instagram.',
      'META_SESSION_INVALID': '🔑 Session Meta invalide. Reconnectez Instagram.'
    };
    return metaMessages[metaError.errorCode] || metaError.message || 'Erreur Meta - Reconnexion requise';
  }
  
  return formatApiError(error).message;
}

/**
 * P0 FIX: Build Meta OAuth error toast with CTA buttons
 * @param {Object} metaError - Meta OAuth error info from isMetaOAuthError()
 * @returns {Object} Toast config with title, description, and CTA actions
 */
export function buildMetaOAuthToast(metaError) {
  if (!metaError) return null;
  
  const titleMap = {
    'META_TOKEN_INVALID': 'Token Meta Invalide',
    'META_APP_ID_MISMATCH': 'Application Meta Incorrecte',
    'META_TOKEN_EXPIRED': 'Token Meta Expiré',
    'META_PASSWORD_CHANGED': 'Mot de Passe Facebook Modifié',
    'META_SESSION_INVALID': 'Session Meta Invalide'
  };
  
  const descriptionMap = {
    'META_TOKEN_INVALID': 'Meta a révoqué l\'accès ou vous n\'avez pas autorisé l\'application.',
    'META_APP_ID_MISMATCH': 'Le token a été généré par une autre application Meta.',
    'META_TOKEN_EXPIRED': 'Le token d\'accès a expiré après 60 jours.',
    'META_PASSWORD_CHANGED': 'Votre mot de passe Facebook a été modifié.',
    'META_SESSION_INVALID': 'Votre session Facebook/Instagram a expiré.'
  };
  
  return {
    title: titleMap[metaError.errorCode] || 'Erreur Meta OAuth',
    description: descriptionMap[metaError.errorCode] || metaError.message,
    errorCode: metaError.errorCode,
    stage: metaError.stage,
    requestId: metaError.requestId,
    metaErrorCode: metaError.metaErrorCode,
    nextSteps: metaError.nextSteps,
    // P0 FIX: CTA actions for Meta OAuth errors
    ctaActions: [
      { label: 'Déconnecter (Hard Reset)', action: 'DISCONNECT_INSTAGRAM', variant: 'destructive' },
      { label: 'Reconnecter', action: 'RECONNECT_WITH_REREQUEST', variant: 'default' }
    ],
    requiresReconnect: true
  };
}

/**
 * P0 LOCKDOWN: Build structured toast content from ErrorEnvelope
 * ALWAYS includes error_code + stage + request_id in description
 * @param {Error|Object} error - The error object or errorInfo
 * @returns {Object} { title, description, errorCode, stage, requestId, nextSteps, isStructured }
 */
export function buildStructuredToast(error) {
  // Extract errorInfo from error object
  const errorInfo = error?.errorInfo || error?.response?.data || error;
  
  // P0 LOCKDOWN: Always try to extract structured fields
  const errorCode = errorInfo?.error_code || errorInfo?.error || 'UNKNOWN';
  const stage = errorInfo?.stage || 'UNKNOWN';
  const requestId = errorInfo?.request_id || errorInfo?.requestId || `req_local_${Date.now()}`;
  const correlationId = errorInfo?.correlation_id || `corr_local_${Date.now()}`;
  const message = errorInfo?.message || 'Une erreur est survenue';
  const nextSteps = errorInfo?.next_steps || [];
  
  const isStructured = !!(
    errorInfo?.error_code || 
    errorInfo?.stage || 
    errorInfo?.isErrorEnvelope
  );
  
  // P0 LOCKDOWN: ALWAYS build description with error_code + stage + request_id
  const description = `Code: ${errorCode} | Stage: ${stage} | ID: ${requestId.substring(0, 16)}`;
  
  return {
    title: message,
    description,  // P0 LOCKDOWN: Always includes structured info
    errorCode,
    stage,
    requestId,
    correlationId,
    nextSteps,
    debugInfo: errorInfo?.debug_info,
    isStructured,
    isSyntheticEnvelope: errorInfo?.isSyntheticEnvelope || false
  };
}

/**
 * P0 FIX: Check if error is an ErrorEnvelope (has structured fields)
 * @param {Error|Object} error - The error object
 * @returns {boolean}
 */
export function isErrorEnvelope(error) {
  const errorInfo = error?.errorInfo || error?.response?.data || error;
  return !!(errorInfo?.error_code || errorInfo?.stage || errorInfo?.next_steps);
}

/**
 * P0 POST CHANNEL: Check if error is a 405 Method Not Allowed error
 * @param {Error|Object} error - The error object
 * @returns {Object|null} Error info if 405 error, null otherwise
 */
export function is405Error(error) {
  const errorInfo = error?.errorInfo || error?.response?.data;
  if (error?.response?.status === 405 || errorInfo?.error_code === 'HTTP_405' || errorInfo?.is405Error) {
    return {
      errorCode: 'HTTP_405',
      message: errorInfo?.message || 'Méthode non autorisée',
      stage: errorInfo?.stage || 'MIDDLEWARE_PROTECTION',
      requestId: errorInfo?.request_id || errorInfo?.requestId,
      isPrefetch: errorInfo?.isPrefetch || errorInfo?.debug_info?.is_prefetch_likely,
      suspect: errorInfo?.debug_info?.suspect,
      path: errorInfo?.debug_info?.path,
      method: errorInfo?.debug_info?.method,
      nextSteps: errorInfo?.next_steps || ['Rafraîchissez la page'],
    };
  }
  return null;
}

/**
 * P0 POST CHANNEL: Build toast content for 405 errors
 * @param {Object} error405 - 405 error info from is405Error()
 * @returns {Object} Toast config with title, description, and suggestions
 */
export function build405Toast(error405) {
  if (!error405) return null;
  
  const titleMap = {
    'safari_prefetch': 'Prefetch Safari détecté',
    'link_preview': 'Prévisualisation de lien',
    'crawler_bot': 'Robot détecté',
    'no_user_agent': 'Requête sans identifiant',
    'client_bug': 'Erreur client détectée',
  };
  
  const descriptionMap = {
    'safari_prefetch': 'Safari a envoyé une requête HEAD/GET pour précharger le lien. Action non exécutée.',
    'link_preview': 'Une application a tenté de prévisualiser ce lien.',
    'crawler_bot': 'Un robot a tenté d\'accéder à cette route.',
    'no_user_agent': 'Une requête sans User-Agent a été bloquée.',
    'client_bug': 'Le client a envoyé une requête GET au lieu de POST.',
  };
  
  const suspect = error405.suspect || 'client_bug';
  
  return {
    title: titleMap[suspect] || 'Méthode non autorisée',
    description: descriptionMap[suspect] || error405.message,
    errorCode: error405.errorCode,
    stage: error405.stage,
    requestId: error405.requestId,
    path: error405.path,
    method: error405.method,
    isPrefetch: error405.isPrefetch,
    nextSteps: error405.nextSteps,
    // Suggestion: Refresh page or disable prefetch
    suggestion: error405.isPrefetch 
      ? 'Cette requête est normale (prefetch navigateur). Ignorez ce message.'
      : 'Rafraîchissez la page et réessayez.',
  };
}

// =============================================================================
// P0 DEFINITIVE: Re-export postOnlyRoutes utilities for convenience
// =============================================================================
export {
  POST_ONLY_ROUTES,
  GET_SAFE_ROUTES,
  detectInvalidMethodOnRoute,
  isPostOnlyRoute,
  isGetSafeRoute,
} from './postOnlyRoutes';

// =============================================================================
// P0 iOS Safari DUAL-HEADER: Expose apiClient for testAuthEcho
// =============================================================================
if (typeof window !== 'undefined') {
  window.__apiClient = apiClient;
}
